import { getAppType, getAppVersion } from '@/shared/bridge';
import type { PlatformVersion, VersionManifest } from '../../../versions/version.type';

/**
 * 버전 판정 결과 — 세 상태로 접는다:
 *
 * - `update-required`  — 현재 버전 < `minimumVersion`. 하위호환이 끊긴 구버전 → **강제 업데이트**.
 * - `update-available` — 현재 버전 < `latestVersion`(≥ minimum). 아직 쓸 수는 있는 구버전 → **넛지(선택) 업데이트**.
 * - `up-to-date`       — 최신.
 *
 * 강제(minimum) 우선 — minimum 미만이면 latest 비교와 무관하게 `update-required`.
 */
export type VersionStatus = 'up-to-date' | 'update-available' | 'update-required';

/** 판정에 쓰인 매니페스트 축 — `getAppType()==='web'` → `'web'`, 아니면(`ios`·`android`) → `'native'`. */
export type VersionPlatform = 'web' | 'native';

export interface VersionCheckResult {
  status: VersionStatus;
  /** 어느 매니페스트 축으로 판정했는지. */
  platform: VersionPlatform;
  currentVersion: string;
  latestVersion: string;
  minimumVersion: string;
}

/**
 * semver core 비교. `a < b` → `-1`, `a === b` → `0`, `a > b` → `1`.
 *
 * pre-release/build 메타(`-rc.1`, `+build`)는 **떼고 숫자 core(`major.minor.patch`)만** 본다 — 버전 게이트는
 * 정식 배포 버전끼리의 대소만 필요하고, pre-release 우선순위 규칙(semver 11)까지 구현하면 과하다.
 * 세그먼트 개수가 다르면 짧은 쪽을 0 으로 채워 비교(`1.2` vs `1.2.0` → 동일).
 *
 * 세그먼트가 숫자로 안 읽히면(형태 이상) 0 으로 간주 — 매니페스트는 이미 `parseManifest` 를 통과한 값이라
 * 여기까지 오면 정상 semver 전제. 방어적으로만 NaN 을 0 으로 접는다.
 */
export function compareSemver(a: string, b: string): -1 | 0 | 1 {
  const pa = parseCore(a);
  const pb = parseCore(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na < nb) return -1;
    if (na > nb) return 1;
  }
  return 0;
}

function parseCore(version: string): number[] {
  // pre-release(`-`)·build(`+`) 메타 컷 → 숫자 core 만.
  const core = version.trim().split(/[-+]/, 1)[0] ?? '';
  return core.split('.').map((seg) => {
    const n = Number.parseInt(seg, 10);
    return Number.isNaN(n) ? 0 : n;
  });
}

/**
 * 순수 판정 — 현재 버전 하나를 한 플랫폼 축(`PlatformVersion`)에 대고 재본다. I/O·전역 접근 없음(테스트 용이).
 */
export function evaluateVersion(
  currentVersion: string,
  version: PlatformVersion,
  platform: VersionPlatform,
): VersionCheckResult {
  const belowMinimum = compareSemver(currentVersion, version.minimumVersion) < 0;
  const updateAvailable = compareSemver(currentVersion, version.latestVersion) < 0;
  return {
    status: belowMinimum ? 'update-required' : updateAvailable ? 'update-available' : 'up-to-date',
    platform,
    currentVersion,
    latestVersion: version.latestVersion,
    minimumVersion: version.minimumVersion,
  };
}

/**
 * 매니페스트를 **현재 실행 컨텍스트**(플랫폼·앱 버전)에 대고 판정하는 편의 함수.
 *
 * - 플랫폼 축 선택: `getAppType()` 이 `'web'` 이면 `manifest.web`, 아니면(`ios`·`android`) `manifest.native`.
 *   native 는 두 스토어가 단일 semver 를 공유해 축이 하나다(`version.type` 참조).
 * - 현재 버전: `getAppVersion()` (native=앱 버전 / web=빌드 주입 버전).
 *
 * 매니페스트 조회는 `fetchVersionManifest()` 가 담당 — 실패는 거기서 throw, 호출측이 fail-open 으로 감싼다.
 */
export function checkVersion(manifest: VersionManifest): VersionCheckResult {
  const platform: VersionPlatform = getAppType() === 'web' ? 'web' : 'native';
  return evaluateVersion(getAppVersion(), manifest[platform], platform);
}
